/*
  Calm Eden — Sleep Drops listicle: buy options behaviour.

  Ported from the source advertorial's Alpine component. Differences, all forced
  by the Calm Eden catalogue rather than by preference:

  - Purchase type defaults to one-time. The source defaults to `subscription`
    and always attaches a selling plan; this product has no selling plans, so a
    selling plan is attached only when a block actually supplies one. Sending a
    non-existent selling_plan id makes /cart/add.js fail outright.
  - Quantity is per offer, so a pack can be expressed as quantity-of-a-variant
    rather than requiring a dedicated pack variant.
  - Checkout runs through the same validated cart transaction, gift
    reconciliation, and native checkout-form gate as every other storefront
    entry point. It never clears a shopper's existing cart and never navigates
    directly to /checkout.
  - Cart/add failures surface an inline message instead of `alert()`, and the
    button re-enables, so a failed add can be retried.
*/

document.addEventListener('alpine:init', () => {
  Alpine.data('celListicleOffer', (config) => ({
    offers: config.offers || [],
    selectedOffer: config.defaultOffer || 0,
    countdownMinutes: config.countdownMinutes || 15,
    loading: false,
    countdown: '',
    error: '',
    currentSlide: 0,

    init() {
      if (this.countdownMinutes > 0) this.startCountdown(this.countdownMinutes);

      // Restore the button if the browser returns here via the back/forward cache.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.loading = false;
      });

      this.$nextTick(() => this.initSwipers());
    },

    /*
      The bundled Swiper build is the custom-element distribution: it registers
      <swiper-container> and does NOT expose a global `Swiper` constructor, and
      it ships no stylesheet for classic `.swiper` markup. So both sliders are
      <swiper-container> elements rendered with init="false", configured by
      assigning params, then initialised once the definition has landed.
    */
    initSwipers() {
      const mainEl = this.$el.querySelector('[data-cel-swiper-main]');
      const thumbsEl = this.$el.querySelector('[data-cel-swiper-thumbs]');
      if (!mainEl || !thumbsEl) return;

      const src = this.$el.dataset.celSwiperSrc;
      this.loadSwiper(src)
        .then(() => this.startSwipers(mainEl, thumbsEl))
        .catch(() => {});
    },

    loadSwiper(src) {
      if (window.customElements && window.customElements.get('swiper-container')) {
        return Promise.resolve();
      }
      if (!src) return Promise.reject(new Error('missing swiper src'));
      if (!window.calmedenSwiperLoadPromise) {
        window.calmedenSwiperLoadPromise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.defer = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      return window.calmedenSwiperLoadPromise;
    },

    startSwipers(mainEl, thumbsEl) {
      if (this.swipersStarted) return;
      this.swipersStarted = true;

      Object.assign(thumbsEl, {
        spaceBetween: 6,
        slidesPerView: 4,
        freeMode: true,
        watchSlidesProgress: true,
      });
      if (thumbsEl.initialize) thumbsEl.initialize();
      thumbsEl.setAttribute('data-cel-ready', '');

      Object.assign(mainEl, {
        spaceBetween: 10,
        slidesPerView: 1,
        thumbs: { swiper: thumbsEl.swiper },
      });
      if (mainEl.initialize) mainEl.initialize();
      mainEl.setAttribute('data-cel-ready', '');

      if (mainEl.swiper) {
        mainEl.swiper.on('slideChange', () => {
          this.currentSlide = mainEl.swiper.activeIndex;
        });
      }

      const next = this.$el.querySelector('[data-cel-arrow-next]');
      const prev = this.$el.querySelector('[data-cel-arrow-prev]');
      if (next) next.addEventListener('click', () => mainEl.swiper && mainEl.swiper.slideNext());
      if (prev) prev.addEventListener('click', () => mainEl.swiper && mainEl.swiper.slidePrev());
    },

    startCountdown(minutes) {
      let time = minutes * 60;
      const tick = () => {
        const m = Math.floor(time / 60).toString().padStart(2, '0');
        const s = (time % 60).toString().padStart(2, '0');
        this.countdown = `${m}:${s}`;
        if (time <= 0) {
          clearInterval(timer);
          this.countdown = '00:00';
          return;
        }
        time--;
      };
      tick();
      const timer = setInterval(tick, 1000);
    },

    selectOffer(index) {
      this.selectedOffer = index;
      this.error = '';
    },

    get current() {
      return this.offers[this.selectedOffer] || null;
    },

    get currentBtnText() {
      return this.current ? this.current.btnText || '' : '';
    },

    async handleCheckout() {
      const offer = this.current;
      if (!offer) return;

      if (!offer.variantId || offer.valid !== true) {
        this.error = 'This offer is currently unavailable.';
        return;
      }

      this.loading = true;
      this.error = '';

      const item = { id: offer.variantId, quantity: offer.quantity || 1 };
      if (offer.sellingPlanId) item.selling_plan = offer.sellingPlanId;

      const items = [item];

      if (offer.shippingVariantId) {
        items.push({ id: offer.shippingVariantId, quantity: 1 });
      }

      (offer.gifts || []).forEach((gift) => {
        if (gift.variantId) items.push({ id: gift.variantId, quantity: gift.quantity || 1 });
      });

      try {
        const cart = window.CalmEdenCart;
        const bundle = window.CalmEdenBundle;
        if (
          !cart ||
          typeof cart.addItems !== 'function' ||
          !bundle ||
          typeof bundle.reconcile !== 'function'
        ) {
          throw new Error('cart gate unavailable');
        }

        /* Legacy query-string discounts bypass the native checkout gate and
           can silently fail or stack differently from the displayed offer.
           P4's unified Discount Function is authoritative; fail closed if a
           stale theme block still carries a code. */
        const codes = [];
        if (offer.discountCode) codes.push(offer.discountCode);
        (offer.gifts || []).forEach((gift) => {
          if (gift.discountCode) codes.push(gift.discountCode);
        });
        if (codes.length) throw new Error('legacy discount code configured');

        const added = await cart.addItems(
          {
            items,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname,
          },
          [],
          null,
          {
            settle() {
              return bundle.reconcile().then((changed) => {
                if (changed && typeof cart.refreshDrawerNow === 'function') {
                  return cart.refreshDrawerNow();
                }
                return null;
              });
            },
          },
        );
        if (added !== true) throw new Error('validated add failed');

        /* Re-acquire the current server-rendered drawer form and its owned
           submitter: addItems may have replaced both. requestSubmit triggers
           cart-drawer.js's final reconcile gate and then one native Shopify
           handoff; no direct /checkout navigation exists on this path. */
        const checkoutForm = document.getElementById('CartDrawer-Form');
        const checkoutButton = document.getElementById('CartDrawer-Checkout');
        if (
          !checkoutForm ||
          !checkoutButton ||
          checkoutButton.form !== checkoutForm ||
          typeof checkoutForm.requestSubmit !== 'function'
        ) {
          throw new Error('checkout form unavailable');
        }
        checkoutForm.requestSubmit(checkoutButton);
      } catch (error) {
        this.error = 'We could not prepare checkout. Please try again.';
        this.loading = false;
      }
    },
  }));
});
